import React, {
  FC,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';
import {
  formatOrderNumbers,
  getValueLabelFromStringArray,
  orderTransformer,
  tOption,
  tOrder,
  zOrder,
} from './zod';
import { useApi, useRequiredContext } from '../hooks';

import { useAppInit } from './appInitContext';

const zQueryInput = z.object({
  orderNumbers: z.string().array(),
  orderLines: z.string().array().optional(),
  customerNumber: z.string().optional(),
  email: z.string().optional().optional(),
});

const zContext = z.object({
  getOrder: z.function().args(zQueryInput).returns(z.promise(zOrder.array())),
  loading: z.boolean(),
  orders: zOrder.array().optional(),
  customerOrderNumbers: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .array()
    .nullable(),
  search: z.string(),
  setSearch: z.function().args(z.string()).returns(z.void()),
  setDefaultOrder: z.function().args(z.string()).returns(z.void()),
  defaultOrder: z.string(),
  defaultCustomerNumber: z.string(),
});

const getOrderLines = (orderLines) => {
  if (orderLines) {
    return formatOrderNumbers(orderLines, true);
  }
  return '';
};

const getCustomerOrEmailParams = (customerNumber, email) => {
  let result = '';
  if (customerNumber && email) {
    result = `customerNumber=${customerNumber}&email=${email}`;
  } else if (customerNumber) {
    result = `customerNumber=${customerNumber}`;
  } else if (email) {
    result = `email=${email}`;
  }
  return result;
};

type tContext = z.infer<typeof zContext>;
type tQueryInput = z.infer<typeof zQueryInput>;

const OrderContext = createContext<tContext>(undefined);

export const OrderProvider: FC = ({ children }) => {
  const api = useApi();
  const { params } = useAppInit();

  const {
    customerNumber: initCustomerNumber,
    email: initEmail,
    initOrderKeys,
    initOrderLines,
    allowOrderFiltering,
    allowOrderRowFiltering,
    returnType,
  } = params;

  const [orders, setOrders] = useState<tOrder[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(undefined);
  const [defaultOrder, setDefaultOrder] = useState<string | undefined>(
    initOrderKeys && initOrderKeys[0] ? initOrderKeys[0] : undefined,
  );
  const [defaultCustomerNumber, setDefaultCustomerNumber] = useState<
    string | undefined
  >();
  const [customerOrderNumbers, setCustomerOrderNumbers] = useState<
    tOption[] | undefined
  >(undefined);

  const getOrder = useCallback(
    async ({
      orderNumbers,
      orderLines,
      email,
      customerNumber,
    }: tQueryInput) => {
      setLoading(true);

      const response = await api(
        `digitalorders?${getCustomerOrEmailParams(
          customerNumber,
          email,
        )}&allowOrderFiltering=${allowOrderFiltering}&allowOrderRowFiltering=${allowOrderRowFiltering}&orders=${formatOrderNumbers(
          orderNumbers,
          false,
        )}${getOrderLines(orderLines)}&returnType=${returnType}`,
        'GET',
        'no-cors',
      );

      if (response.status === 'Failure') {
        setLoading(false);
        return response;
      }

      if (response.data.customerOrderNumbers) {
        setCustomerOrderNumbers(
          getValueLabelFromStringArray(response.data.customerOrderNumbers),
        );
        setDefaultOrder(response.data.orders[0].orderNumber);
      } else {
        setCustomerOrderNumbers(
          getValueLabelFromStringArray([response.data.orders[0].orderNumber]),
        );
        setDefaultOrder(response.data.orders[0].orderNumber);
      }
      const result = orderTransformer(response.data);
      if (!defaultCustomerNumber && result[0] && result[0].customerNumber) {
        setDefaultCustomerNumber(result[0].customerNumber);
      }
      setOrders(result);
      setLoading(false);
      return result;
    },
    [defaultCustomerNumber, allowOrderFiltering, allowOrderRowFiltering, api],
  );

  useEffect(() => {
    if (initOrderKeys && !orders) {
      getOrder({
        orderNumbers: initOrderKeys,
        orderLines: initOrderLines,
        email: initEmail,
        customerNumber: initCustomerNumber,
      });
    }
  }, [
    orders,
    initOrderKeys,
    defaultOrder,
    getOrder,
    initCustomerNumber,
    initEmail,
    initOrderLines,
  ]);

  const currentContext = useMemo(
    () => ({
      orders,
      getOrder,
      loading,
      defaultOrder,
      defaultCustomerNumber,
      customerOrderNumbers,
      search,
      setSearch,
      setDefaultOrder,
    }),
    [
      search,
      loading,
      orders,
      customerOrderNumbers,
      getOrder,
      setDefaultOrder,
      defaultOrder,
      defaultCustomerNumber,
    ],
  );

  OrderContext.displayName = 'OrderProvider';
  return (
    <OrderContext.Provider value={currentContext}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => useRequiredContext(OrderContext);
